/*
================================================================================
File: src/components/auth/AuthForm.js
Description: Component for user login and registration forms.
================================================================================
*/
import React, { useState } from 'react';
import moment from 'moment';

const AuthForm = ({ title, buttonText, secondaryText, onSwitchView, onAuthSubmit, error, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onAuthSubmit(email, password);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 ">
      {/* Sunset Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-300 dark:border-amber-700 px-4 py-2 text-center">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Important Notice:</strong> This app is being sunsetted and will transition to the{" "}
          <strong>FlippingPro app</strong>, available in the App Store. All future support will be through{" "}
          <a
            href="mailto:support@FlippingPro.com"
            className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-100"
          >
            support@FlippingPro.com
          </a>
          .
        </p>
      </div>
      <div className="flex-grow flex items-center justify-center p-4 ">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 dark:border dark:border-gray-700">
          <div className="text-center">
            <img
              src="./images/logo-dark.png"
              alt="logo"
              className="h-16 dark:hidden"
            />
            <img
              src="./images/logo-light.png"
              alt="logo"
              className="h-16 hidden dark:inline"
            />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-800 dark:text-gray-200">
              {title}
            </h2>
          </div>
          {loading && (
            <div className="flex justify-center items-center h-24">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              <span className="ml-4 text-blue-500 text-lg dark:text-gray-200">Loading...</span>
            </div>
          )}
          {error && (
            <div className="flex p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 mt-7" role="alert">
              <svg className="shrink-0 inline w-4 h-4 me-3 mt-[2px]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
              </svg>
              <div>
                <span className="font-medium">Login Error:</span>
                <ul className="mt-1.5 list-disc list-inside">
                  <li>{error}</li>
                </ul>
              </div>
            </div>
          )}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">Email address</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:border-gray-700 dark:text-gray-200 dark:bg-gray-900 dark:hover:bg-gray-900"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="pt-2">
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:border-gray-700 dark:text-gray-200 dark:bg-gray-900 dark:hover:bg-gray-900"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:border-gray-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
              >
                {buttonText}
              </button>
            </div>
          </form>
          <div className="mt-6 text-center">
            <button onClick={onSwitchView} className="font-medium text-blue-600 hover:text-blue-500">
              {secondaryText}
            </button>
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4 p-4">
            <div>
              <a href="https://apps.apple.com/us/app/id1484421845" target="_blank" rel="noreferrer">
                <img width="150" src="https://i0.wp.com/hazelegal.com.au/wp-content/uploads/2020/10/App-Store.png?resize=239%2C80&ssl=1" alt="Download on the App Store" />
              </a>
            </div>
            <div>
              <a href="https://play.google.com/store/apps/details?id=com.loveamericanhomes.BigDogToolsApp" target="_blank" rel="noreferrer">
                <img width="150" src="https://www.pngkey.com/png/full/203-2031321_app-store-icon-play-store-icon-google-logo.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <footer className="w-full bg-white dark:bg-gray-800 p-4 text-center border-t border-gray-200 dark:border-gray-700 mt-auto">
        <p className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
          <span className="flex-1 text-center md:text-left">
            COPYRIGHT &copy; {moment().format("YYYY")}
            <a className="ml-1 text-blue-600 hover:text-blue-500" href="https://loveamericanhomes.com" target="_blank" rel="noreferrer">
              Love American Homes
            </a>
            <span className="hidden sm:inline-block">, All rights Reserved</span>
          </span>
          <span className="hidden md:block">Hand-crafted & Made with ❤️</span>
        </p>
      </footer>
    </div>
  );
};

export default AuthForm;